/**
 * Servicio para interactuar con la IA de generación de formularios
 */

interface AIMessage {
    role: "user" | "assistant" | "system";
    content: string;
}

interface AIResponse {
    choices: Array<{
        message: {
            role: string;
            content: string;
        };
    }>;
}

export class AIFormBuilderService {
    private readonly AI_API_URL =
        "https://public-api.devexpress.com/demo-openai/openai/deployments/gpt-4o-mini/chat/completions?api-version=2024-02-01";
    private readonly API_KEY = "DEMO";
    public conversationHistory: AIMessage[] = [];

    constructor() {
        this.initializeSystemPrompt();
    }

    /**
     * Inicializa el prompt del sistema con contexto sobre el form builder
     */
    private initializeSystemPrompt() {
        const systemPrompt = `Eres un asistente especializado en crear y editar formularios dinámicos para una aplicación Vue.js + TypeScript.

Tu trabajo es:
1. Hacer preguntas al usuario para entender qué formulario necesita O qué cambios quiere hacer
2. Si el usuario menciona una API (ej: "/clientes"), pedirás que se consulte primero
3. Generar el JSON de configuración del formulario siguiendo esta estructura exacta:

RECONOCIMIENTO DE INTENCIÓN DEL USUARIO:
Cuando el usuario diga cualquiera de estas frases, debes generar el formulario inmediatamente:
- "guarda", "guardar", "crea", "crear", "genera", "generar", "hazlo", "adelante"
- "sí", "si", "ok", "vale", "perfecto", "correcto", "está bien"
- "procede", "continúa", "continua"

TIPOS DE COMPONENTES DISPONIBLES (usar el NÚMERO, no el nombre):
- 0 = TextBox (campo de texto simple)
- 1 = MultiCombo (lista desplegable múltiple)
- 2 = SingleCombo (lista desplegable simple)
- 3 = Date (selector de fecha)
- 4 = TextArea (área de texto multilínea)
- 5 = Switch (interruptor booleano)
- 6 = RadioButton (botones de radio)
- 8 = DateTime (selector de fecha y hora)

IMPORTANTE: componentType SIEMPRE debe ser un NÚMERO (0-8), NO un string.

VALIDACIONES CON VEE-VALIDATE 2.X:
El campo "validacion" usa reglas de vee-validate 2.1.3. Sintaxis: "regla1|regla2|regla3:parametro"

Reglas disponibles:
- required: Campo obligatorio
- email: Validar email  
- min:N: Mínimo N caracteres
- max:N: Máximo N caracteres
- numeric: Solo números
- alpha:  Solo letras
- alpha_num: Letras y números
- alpha_dash: Letras, números, guiones y guiones bajos
- alpha_spaces: Letras y espacios
- between:min,max: Valor entre min y max
- confirmed:campo: Debe coincidir con otro campo
- digits:N: Exactamente N dígitos
- min_value:N: Valor mínimo N
- max_value:N: Valor máximo N
- regex:patron: Expresión regular personalizada

Ejemplos de validación:
- Email obligatorio: "required|email"
- Teléfono: "required|numeric|min:9|max:15"
- Nombre: "required|alpha_spaces|min:3|max:50"
- Edad: "required|numeric|min_value:18|max_value:120"
- Código postal: "required|digits:5"
- Usuario: "required|alpha_num|min:4|max:20"
- Sin validación: "" (string vacío)

ESTRUCTURA COMPLETA DEL FORMULARIO (¡MUY IMPORTANTE!):
{
  "action": "generate_form",
  "config": {
    "formName": "Nombre del Formulario",
    "description": "Descripción del formulario",
    "tabs": [
      {
        "id": 1,
        "caption": "General",
        "icon": "mdi-form-select"
      }
    ],
    "fields": [
      {
        "id": "campo_unico_id",
        "caption": "Etiqueta visible",
        "variableName": "nombreVariable",
  "datatype": "string|number|boolean|date|datetime",
        "componentType": 0,
        "defaultValue": null,
  "required": true|false,
        "readonly": false,
        "visible": true,
        "mayuscula": false,
        "maxLength": 255,
        "icon": null,
        "validacion": "required|alpha_spaces",
        "dim_cols": 12,
        "dim_sm": 6,
        "dim_md": 4,
        "intab": 1
      }
    ]
  }
}

CAMPOS ESPECIALES POR TIPO:
- Switch (5): agregar "switchOn": "Sí", "switchOff": "No"
- Date (3): agregar "minDate": "2024-01-01" (opcional)
- SingleCombo (2) o MultiCombo (1): agregar "comboConfig": {
    "dataSource": [],
    "displayExpr": "nombre",
    "valueExpr": "id",
    "apiEndpoint": "/ruta",
    "endpointType": "backend"
  }
- RadioButton (6): agregar "radioOptions": [{"label": "Opción 1", "value": 1}]

EJEMPLOS DE componentType CORRECTO:
- Campo de texto: "componentType": 0
- Combo simple: "componentType": 2
- Fecha: "componentType": 3
- Switch: "componentType": 5

IMPORTANTE - FORMATO DE RESPUESTA:
- Cuando necesites datos de una API, devuelve SOLO: {"action": "fetch_api", "endpoint": "/ruta"}
- Cuando generes el formulario, devuelve el JSON COMPLETO con esta estructura:
  {
    "action": "generate_form",
    "config": {
      "formName": "...",
      "description": "...",
      "tabs": [...],
      "fields": [...]
    }
  }
- NO agregues texto adicional antes o después del JSON cuando devuelvas una acción
- El JSON debe ser válido y parseable
- SIEMPRE usa números para componentType, NUNCA strings
- TODO campo debe tener "icon": null si no tiene icono específico
- La propiedad "validacion" debe ser un string con reglas de vee-validate 2.x separadas por |
- SIEMPRE incluye formName, description, tabs y fields en la configuración`;

        this.conversationHistory = [
            {
                role: "system",
                content: systemPrompt,
            },
        ];
    }

    /**
     * Envía un mensaje a la IA y obtiene respuesta
     */
    public async sendMessage(userMessage: string): Promise<string> {
        console.log("📤 Enviando mensaje a IA:", userMessage);

        this.conversationHistory.push({
            role: "user",
            content: userMessage,
        });

        try {
            const response = await fetch(this.AI_API_URL, {
                method: "POST",
                headers: {
                    "api-key": this.API_KEY,
                    "Content-Type": "application/json",
                    accept: "application/json",
                },
                body: JSON.stringify({
                    messages: this.conversationHistory,
                    model: "gpt-4o-mini",
                    max_tokens: 2000,
                    temperature: 0.7,
                }),
            });

            if (!response.ok) {
                throw new Error(`Error HTTP: ${response.status}`);
            }

            const data: AIResponse = await response.json();
            const assistantMessage = data.choices[0].message.content;

            console.log("📥 Respuesta de IA:", assistantMessage);

            this.conversationHistory.push({
                role: "assistant",
                content: assistantMessage,
            });

            return assistantMessage;
        } catch (error) {
            console.error("Error comunicación con IA:", error);
            throw new Error("No se pudo conectar con el asistente de IA");
        }
    }

    /**
     * Proporciona contexto de datos de API a la IA
     */
    public async provideApiContext(endpoint: string, data: any): Promise<string> {
        const contextMessage = `He consultado la API "${endpoint}" y obtuve estos datos de ejemplo:

\`\`\`json
${JSON.stringify(data.slice(0, 3), null, 2)}
\`\`\`

Campos disponibles: ${Object.keys(data[0] || {}).join(", ")}

Por favor, continúa con la generación del formulario usando estos campos.`;

        return await this.sendMessage(contextMessage);
    }

    /**
     * Proporciona contexto de un formulario existente para edición
     */
    public async provideFormContext(formConfig: any): Promise<void> {
        const contextMessage = `El usuario quiere EDITAR un formulario existente. Aquí está la configuración actual:

\`\`\`json
${JSON.stringify(formConfig, null, 2)}
\`\`\`

Cuando el usuario pida cambios:
1. Pregunta QUÉ quiere cambiar específicamente
2. Mantén TODO lo que no debe cambiar
3. Solo modifica lo solicitado
4. Cuando termines, genera el formulario COMPLETO con los cambios aplicados

Recuerda: debes devolver el JSON completo del formulario, no solo los cambios.`;

        // Enviar contexto sin esperar respuesta visible
        this.conversationHistory.push({
            role: "user",
            content: contextMessage,
        });

        this.conversationHistory.push({
            role: "assistant",
            content: "Entendido. Estoy listo para ayudarte a editar este formulario. ¿Qué cambios te gustaría hacer?",
        });
    }

    /**
     * Reinicia la conversación
     */
    public reset() {
        this.initializeSystemPrompt();
    }

    /**
     * Obtiene el historial de conversación
     */
    public getHistory(): AIMessage[] {
        return this.conversationHistory.filter((msg) => msg.role !== "system");
    }

    /**
     * Intenta extraer una acción JSON de la respuesta de la IA
     */
    public extractAction(message: string): any {
        console.log("🔍 Buscando acción en mensaje:", message);

        try {
            // Buscar bloques JSON en el mensaje
            const jsonMatch = message.match(/```json\s*([\s\S]*?)\s*```/) || message.match(/\{[\s\S]*\}/);

            if (jsonMatch) {
                const jsonStr = jsonMatch[1] || jsonMatch[0];
                console.log("📋 JSON encontrado:", jsonStr);
                const parsed = JSON.parse(jsonStr);
                console.log("✅ Acción parseada:", parsed);
                return parsed;
            }

            console.log("⚠️ No se encontró JSON en el mensaje");
        } catch (e) {
            console.error("❌ Error parseando JSON:", e);
        }
        return null;
    }
}
